/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useEffect, useMemo, useRef } from 'react';
import { MentionsInput, Mention, OnChangeHandlerFunc } from 'react-mentions';
import { makeStyles, tokens } from '@fluentui/react-components';

import { CARD_TEXTAREA_PLACEHOLDER, MENTIONS_CHILD_CLASSNAMES, MENTIONS_ROOT_CLASSNAME } from '../../constants';

import { useUsers } from '@modules/users';

// Notice: styling with Tailwind is not possible.
// Got inspired to use Fluent UI instead by this example: https://shwiloo.medium.com/styling-a-react-mentions-component-with-jss-e5d2a89d3ce3

const paddingTop = '3px';
const paddingBottom = '2px';
const padding = `${paddingTop} 2px ${paddingBottom} 2px`;
const fontSize = '14px';

const useStyles = makeStyles({
  root: {
    width: '100%',
    height: '100%',

    // MentionsInput container
    [`& ${MENTIONS_CHILD_CLASSNAMES.CONTROL}`]: {
      fontSize,
    },

    // Highlighter (behind typed text)
    [`& ${MENTIONS_CHILD_CLASSNAMES.HIGHLIGHTER}`]: {
      padding,
      border: '2px inset transparent',
    },

    // Input area
    [`& ${MENTIONS_CHILD_CLASSNAMES.INPUT}`]: {
      padding,
      border: '0',
      borderBottom: `1px solid ${tokens.colorNeutralStroke1}`,
      color: '#323130',
      overflowWrap: 'break-word',

      // Remove default focus outline
      '&:focus': {
        outline: 'none',
      },
    },

    // Suggestions dropdown
    [`& ${MENTIONS_CHILD_CLASSNAMES.SUGGESTIONS}`]: {
      marginTop: `${paddingTop + paddingBottom}px`,
      border: `1px solid ${tokens.colorNeutralStroke1}`,
      boxShadow: '0 2px 4px 0 rgba(0,0,0,0.2)',
      width: 'max-content',
      maxHeight: '300px',
      overflow: 'auto',
      // Bring the dropdown to the front
      zIndex: '100 !important',
    },

    // Suggestion list items
    [`& ${MENTIONS_CHILD_CLASSNAMES.SUGGESTIONS_ITEM}`]: {
      display: 'flex',
      alignItems: 'center',
      transition: 'background-color 0.2s',
    },

    // Focused suggestion item
    [`& ${MENTIONS_CHILD_CLASSNAMES.SUGGESTIONS_ITEM_FOCUSED}`]: {
      backgroundColor: tokens.colorNeutralBackground1,
    },
  },
  mention: {
    position: 'relative',
    zIndex: '1',
    color: tokens.colorBrandBackground,
    backgroundColor: `${tokens.colorBrandBackground} !important`,
    textDecoration: 'none',
    pointerEvents: 'none',
    mixBlendMode: 'color',
  },
});

type TProps = {
  value: string;
  onChange: (newValue: string) => void;
  onFocus: () => void;
  onBlur: () => void;
  autoFocus?: boolean;
};

const MentionsTextarea: React.FC<TProps> = ({ value, onChange, onFocus, onBlur, autoFocus = true }) => {
  const classes = useStyles();

  // Pull the list of users from your hook
  const { users, userMap } = useUsers();

  // Prepare data for react-mentions
  const mentionUsers = useMemo(
    () =>
      (users || [])
        .filter((user) => user.inviteAccepted)
        .map((user) => ({
          id: user._id,
          display: user.name,
        })),
    [users]
  );

  // Handle text change
  const handleChange: OnChangeHandlerFunc = (_event, newValue, _newPlainTextValue: string, _mentions: any[]) => {
    onChange(newValue);
  };

  const refTextarea = useRef<HTMLTextAreaElement>(null);
  const refValue = useRef(value);
  refValue.current = value;

  const handleKeyDown = (event: React.KeyboardEvent<HTMLTextAreaElement> | React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter') {
      if (!refTextarea.current) return;

      const { selectionStart, selectionEnd, value } = refTextarea.current;
      // const newValue = refValue.current.substring(0, selectionStart) + '\n' + refValue.current.substring(selectionEnd);
      if (selectionStart === selectionEnd && selectionEnd === value.length) {
        // only allow enter at the end of the text
        onChange((refValue.current += '\n'));
        refTextarea.current.selectionStart = refTextarea.current.selectionEnd = selectionEnd + 1;
      }
    }
  };

  useEffect(() => {
    /**
     * Delay the focus to the next tick to avoid the focus being lost.
     * Otherwise tiptap editor will steal the focus.
     */
    requestAnimationFrame(() => {
      if (refTextarea.current) {
        refTextarea.current.focus();
      }
    });
  }, []);

  return (
    // Wrapping the MentionsInput in a div to apply our custom styles
    <div className={classes.root}>
      <MentionsInput
        value={value}
        onChange={handleChange}
        placeholder={CARD_TEXTAREA_PLACEHOLDER}
        // apply our custom className
        className={MENTIONS_ROOT_CLASSNAME}
        onFocus={onFocus}
        onBlur={onBlur}
        autoFocus={autoFocus}
        onKeyDown={handleKeyDown}
        inputRef={refTextarea}
      >
        <Mention
          trigger="@"
          // The default internal markup is __id__.
          // Hence we need to wrap the default __id__ in additional __,
          // which is then replaced by the renderMentions() function in the Comment.tsx component.
          markup="____id____"
          data={mentionUsers}
          displayTransform={(id) => `@${(userMap && userMap[id]?.name) ?? 'Unknown user'}`}
          className={classes.mention}
        />
      </MentionsInput>
    </div>
  );
};

export default MentionsTextarea;
